/**
 * Route Price Detail Page Component
 * /admin/route-prices/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import apiClient from '../api/client';

const RoutePriceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [routePrice, setRoutePrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRoutePrice();
  }, [id]);

  const loadRoutePrice = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getRoutePrice(id);
      setRoutePrice(data);
    } catch (err) {
      setError(err.message || 'Failed to load route price details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !routePrice) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/route-prices')}>
          Back to Route Prices
        </Button>
      </Container>
    );
  }

  if (!routePrice) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6">Route price not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/route-prices')}>
          Back to Route Prices
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/route-prices')}
            sx={{ mb: 1 }}
          >
            Back to Route Prices
          </Button>
          <Typography variant="h4" component="h1">
            Route Price Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Route Price ID: {routePrice.id.substring(0, 8)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/route-prices/${routePrice.id}/edit`)}
        >
          Edit Route Price
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Route Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Price List:
                </Typography>
                <Typography variant="body1">
                  {routePrice.price_list_name || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Pickup Location:
                </Typography>
                <Typography variant="body1">
                  {routePrice.pickup_location?.name || '-'}
                </Typography>
                {routePrice.pickup_location?.address && (
                  <Typography variant="body2" color="text.secondary">
                    {routePrice.pickup_location.address}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Dropoff Location:
                </Typography>
                <Typography variant="body1">
                  {routePrice.dropoff_location?.name || '-'}
                </Typography>
                {routePrice.dropoff_location?.address && (
                  <Typography variant="body2" color="text.secondary">
                    {routePrice.dropoff_location.address}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Pricing Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pricing Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Base Price:
                </Typography>
                <Typography variant="h6" color="primary">
                  {routePrice.base_price ? `AED ${parseFloat(routePrice.base_price).toFixed(2)}` : '-'}
                </Typography>
              </Box>
              {routePrice.min_distance && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Min Distance:
                  </Typography>
                  <Typography variant="body1">
                    {routePrice.min_distance} km
                  </Typography>
                </Box>
              )}
              {routePrice.max_distance && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Max Distance:
                  </Typography>
                  <Typography variant="body1">
                    {routePrice.max_distance} km
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Priority:
                </Typography>
                <Chip
                  label={routePrice.priority || 'normal'}
                  color={routePrice.priority === 'high' ? 'error' : routePrice.priority === 'medium' ? 'warning' : 'default'}
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={routePrice.is_active ? 'Active' : 'Inactive'}
                  color={routePrice.is_active ? 'success' : 'default'}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Additional Information */}
        {(routePrice.department || routePrice.cost_center_code || routePrice.notes) && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {routePrice.department && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Department:
                    </Typography>
                    <Typography variant="body1">{routePrice.department}</Typography>
                  </Grid>
                )}
                {routePrice.cost_center_code && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Cost Center Code:
                    </Typography>
                    <Typography variant="body1">{routePrice.cost_center_code}</Typography>
                  </Grid>
                )}
                {routePrice.notes && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary">
                      Notes:
                    </Typography>
                    <Typography variant="body1">{routePrice.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default RoutePriceDetail;

